import { fetchCampaigns } from '@/api/campaignPlaner';
import { Header } from '@/components/Header';
import { WasmLoadErrorFallback } from '@/components/WasmLoadErrorFallback';
import { WithLoader } from '@/components/WithLoader';
import { CampaignPlannerSection } from '@/dashboards/CampaignPlanner/components/CampaignPlannerSection';
import { CampaignPlannerSkeleton } from '@/dashboards/CampaignPlanner/components/views/CampaignPlannerSkeleton';
import { CreateCampaignModal } from '@/dashboards/CampaignPlanner/components/CreateCampaignModal';
import { DeleteCampaignModal } from '@/dashboards/CampaignPlanner/components/DeleteCampaignModal';
import { EmptyState } from '@/dashboards/CampaignPlanner/components/views/EmptyState';
import { RequestReviewModal } from '@/dashboards/CampaignPlanner/components/RequestReviewModal';
import { CampaignPlannerContextProvider } from '@/dashboards/CampaignPlanner/context';
import { useCampaignPlannerUrl } from '@/dashboards/CampaignPlanner/hooks/useCampaignPlannerUrl';
import { useOptimizer } from '@/optimizer/hooks/useOptimizer';
import { useEffect } from 'react';
import {
    keepPreviousData,
    queryOptions,
    useQuery,
} from '@tanstack/react-query';

export const CampaignPlanner = () => {
    const {
        optimizerInitError,
        setDashboardType,
        resetOptimisationRules,
        optimizerReady,
    } = useOptimizer();
    const {
        params: { projectId },
    } = useCampaignPlannerUrl();

    const { data, isLoading, isFetching } = useQuery(
        queryOptions({
            queryKey: ['savedCampaigns', projectId],
            queryFn: () => fetchCampaigns(projectId),
            staleTime: Infinity,
            placeholderData: keepPreviousData,
            enabled: !!projectId,
        }),
    );

    useEffect(() => {
        setDashboardType('campaign');
    }, [setDashboardType]);

    useEffect(() => {
        if (!optimizerReady) {
            return;
        }

        resetOptimisationRules().catch((error) => {
            // eslint-disable-next-line no-console
            console.error('Failed to reset optimizer rules for Campaign Planner:', error);
        });
    }, [optimizerReady, resetOptimisationRules]);

    if (optimizerInitError) {
        return (
            <WasmLoadErrorFallback
                dashboardName="Campaign Planner"
                error={optimizerInitError}
            />
        );
    }

    const campaigns = data || [];

    const hasCampaigns = !isLoading && campaigns?.length > 0;

    return (
        <CampaignPlannerContextProvider>
            <Header
                className="pb-6 px-16 pt-6 h-auto min-h-28"
                title="Campaign Planner"
            ></Header>
            <WithLoader
                isLoading={isLoading}
                isFetching={isFetching}
                loadingMessage="Loading the campaigns ..."
            >
                <div className="pb-8 px-16">
                    {isLoading && <CampaignPlannerSkeleton />}

                    {!isLoading && hasCampaigns && (
                        <CampaignPlannerSection campaigns={campaigns} />
                    )}

                    {!isLoading && !hasCampaigns && <EmptyState />}
                </div>
            </WithLoader>
            <CreateCampaignModal />
            <RequestReviewModal />
            <DeleteCampaignModal />
        </CampaignPlannerContextProvider>
    );
};
